/**
 * StatusPill — canonical status pill primitive.
 *
 * Mockup parity: every status indicator in
 * `reignara_redesign/app/dashboard/page.tsx` follows the same shape:
 *   `rounded-full px-2.5 py-1 text-[10-11px] font-medium uppercase
 *    tracking-[0.15em] bg-X/Y text-Z`
 * Pre-(m.6) the codebase had at least four divergent renderings of
 * the same idiom. This primitive consolidates them.
 *
 * Variants
 * --------
 *   pending     — warning-muted card + warning-text label
 *   approved    — success-muted card + success-text label
 *   rejected    — destructive-muted card + destructive-text label
 *   reimbursed  — primary fill + primary-foreground label
 *   info        — info-muted card + info-text label
 *   neutral     — muted card + muted-foreground label (fallback for
 *                 unmapped statuses)
 *
 * The `<StatusPill>` always carries the eyebrow-sm typography
 * composite (uppercase + tracking + 11px) so consumers cannot drift.
 *
 * Spec: meridian-redesign — PR (m.6) Phase 1
 * Requirements: 14.1, 18.5
 */

import type { JSX } from 'react';
import { cn } from '@/lib/utils';

export type StatusPillVariant =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'reimbursed'
  | 'info'
  | 'neutral';

export interface StatusPillProps {
  variant: StatusPillVariant;
  /** Visible label — short noun-form ("Pending", "Approved"). */
  children: React.ReactNode;
  /** Optional className passthrough. */
  className?: string;
  /** Stable test id; defaults to `status-pill`. */
  'data-testid'?: string;
}

const VARIANT_CLASSES: Record<StatusPillVariant, string> = {
  pending: 'bg-warning-muted text-warning-text',
  approved: 'bg-success-muted text-success-text',
  rejected: 'bg-destructive-muted text-destructive-text',
  reimbursed: 'bg-primary text-primary-foreground',
  info: 'bg-info-muted text-info-text',
  neutral: 'bg-muted text-muted-foreground',
};

export function StatusPill({
  variant,
  children,
  className,
  'data-testid': dataTestId,
}: StatusPillProps): JSX.Element {
  return (
    <span
      data-testid={dataTestId ?? 'status-pill'}
      data-status-variant={variant}
      className={cn(
        'pill-shape',
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}

export default StatusPill;
